// SpanishV2MessageCard.tsx - Result Card
import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Circle, Copy } from "lucide-react";
import {
  SpanishTone,
  getSpanishLaneV4Country,
  SPANISH_LANE_V4_FRAME,
} from "./spanishLaneV4Config";

interface ParsedSection {
  header: string;
  body: string;
}

interface ParsedReply {
  sections: ParsedSection[];
  microAction: string | null;
}

const HEADER_LINE_RE = /^###\s+(.+?)\s*$/gm;

function parseSpanishV4Sections(markdown: string): ParsedReply | null {
  if (!markdown || typeof markdown !== "string") return null;

  const matches: RegExpMatchArray[] = [];
  let m: RegExpExecArray | null;
  const re = new RegExp(HEADER_LINE_RE);
  while ((m = re.exec(markdown)) !== null) {
    matches.push(m);
  }
  if (matches.length < 2) return null;

  const blocks: ParsedSection[] = matches.map((match, idx) => {
    const header = match[1].trim();
    const start = (match.index ?? 0) + match[0].length;
    const end =
      idx + 1 < matches.length ? matches[idx + 1].index : markdown.length;
    const body = markdown.slice(start, end).trim();
    return { header, body };
  });

  const nonEmpty = blocks.filter((b) => b.body.length > 0);
  if (nonEmpty.length < 2) return null;

  // Last block is treated as the micro-action footer line; the rest render
  // as section blocks.
  const microActionBlock = nonEmpty[nonEmpty.length - 1];
  const sections = nonEmpty.slice(0, -1);

  return {
    sections,
    microAction: microActionBlock.body || null,
  };
}

const markdownComponents = {
  p: ({ node, ...props }: any) => <p className="mb-1.5 last:mb-0" {...props} />,
  ul: ({ node, ...props }: any) => (
    <ul className="list-disc pl-5 my-1.5" {...props} />
  ),
  ol: ({ node, ...props }: any) => (
    <ol className="list-decimal pl-5 my-1.5" {...props} />
  ),
  li: ({ node, ...props }: any) => <li className="my-0.5" {...props} />,
  strong: ({ node, ...props }: any) => (
    <strong className="font-semibold" {...props} />
  ),
};

interface SpanishV2MessageCardProps {
  content: string;
  spanishTone: SpanishTone | string | null | undefined;
  timestamp?: string | number | Date;
  onCopy?: () => void;
}

const SpanishV2MessageCard: React.FC<SpanishV2MessageCardProps> = ({
  content,
  spanishTone,
  timestamp,
  onCopy,
}) => {
  const country = getSpanishLaneV4Country(spanishTone);
  const parsed = useMemo(() => parseSpanishV4Sections(content), [content]);

  return (
    <div
      style={{
        border: `1px solid ${country.accentSoft}`,
        borderRadius: SPANISH_LANE_V4_FRAME.cardRadiusPx,
        overflow: "hidden",
        background: "#FFFFFF",
      }}
    >
      {/* Country identity header bar */}
      <div
        className="flex items-center gap-2"
        style={{
          background: `linear-gradient(135deg, ${country.accentColor} 0%, ${country.accentColor}CC 100%)`,
          padding: "10px 18px",
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "#FFFFFF",
            display: "inline-block",
            opacity: 0.9,
          }}
        />
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.04em",
            color: "#FFFFFF",
            textTransform: "uppercase",
          }}
        >
          {country.flagLabel}
        </span>
      </div>

      <div
        style={{
          padding: SPANISH_LANE_V4_FRAME.cardPaddingPx,
          fontFamily: SPANISH_LANE_V4_FRAME.bodyFont,
          lineHeight: SPANISH_LANE_V4_FRAME.lineHeight,
        }}
      >
        {parsed ? (
          <div>
            {parsed.sections.map((section, idx) => (
              <div
                key={`${section.header}-${idx}`}
                style={{
                  marginBottom: SPANISH_LANE_V4_FRAME.paragraphSpacingPx + 6,
                  paddingBottom: SPANISH_LANE_V4_FRAME.paragraphSpacingPx,
                  borderBottom:
                    idx < parsed.sections.length - 1
                      ? `1px solid ${SPANISH_LANE_V4_FRAME.dividerColor}`
                      : "none",
                }}
              >
                <div
                  style={{
                    fontFamily: SPANISH_LANE_V4_FRAME.headerFont,
                    fontSize: 13.5,
                    fontWeight: 700,
                    letterSpacing: "0.03em",
                    color: country.accentColor,
                    textTransform: "uppercase",
                    marginBottom: 6,
                  }}
                >
                  {section.header}
                </div>
                <div style={{ fontSize: 14.5, color: "#2D2D2D" }}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={markdownComponents}
                  >
                    {section.body}
                  </ReactMarkdown>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Graceful fallback — model didn't follow the section format;
          // render the full reply as prose inside the same card chrome.
          <div style={{ fontSize: 14.5, color: "#2D2D2D" }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {content}
            </ReactMarkdown>
          </div>
        )}

        {/* Micro-action footer — signature element, always shown when we
            have a micro-action line from parsing; falls back to the
            country's default label with no body text if parsing failed. */}
        {(parsed?.microAction || !parsed) && (
          <div
            className="flex items-start gap-2"
            style={{
              marginTop: SPANISH_LANE_V4_FRAME.paragraphSpacingPx,
              paddingTop: SPANISH_LANE_V4_FRAME.paragraphSpacingPx,
              borderTop: `1px solid ${SPANISH_LANE_V4_FRAME.dividerColor}`,
            }}
          >
            <Circle
              className="mt-0.5 flex-shrink-0"
              style={{ width: 8, height: 8, color: country.accentColor }}
              fill={country.accentColor}
            />
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.03em",
                  color: country.accentColor,
                  textTransform: "uppercase",
                  marginBottom: 2,
                }}
              >
                {country.microActionLabel}
              </div>
              {parsed?.microAction && (
                <div style={{ fontSize: 13, color: "#5A5A5A" }}>
                  {parsed.microAction}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Timestamp + copy — rendered inside the card, not by the outer chat bubble */}
        {(timestamp || onCopy) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: SPANISH_LANE_V4_FRAME.paragraphSpacingPx,
              paddingTop: SPANISH_LANE_V4_FRAME.paragraphSpacingPx,
              borderTop: `1px solid ${SPANISH_LANE_V4_FRAME.dividerColor}`,
            }}
          >
            <span style={{ fontSize: 12, color: "#8A8A8A" }}>
              {timestamp
                ? new Date(timestamp).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : ""}
            </span>
            {onCopy && (
              <button
                type="button"
                onClick={onCopy}
                title="Copy"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 6,
                  borderRadius: 8,
                  background: "transparent",
                  border: "none",
                  color: "#8A8A8A",
                  cursor: "pointer",
                  transition: "background 0.2s ease, color 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLButtonElement;
                  el.style.background = country.accentSoft;
                  el.style.color = country.accentColor;
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLButtonElement;
                  el.style.background = "transparent";
                  el.style.color = "#8A8A8A";
                }}
              >
                <Copy size={13} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SpanishV2MessageCard;
